/**
* StartGameCommand.js
* 
* Created on Oct 17, 2013, 11:52:42 AM
*
* @author Javier Quevedo-Fernandez for Ranj
**/
import LanguagePack from '../../../../lib/html5-software-library/src/ranj/language/LanguagePack';
import TrackString from '../../analytics/TrackString';
import ChapterLibrary from '../../app/chapters/model/ChapterLibrary';
import InitializeCharactersEvent from '../../app/characters/event/InitializeCharactersEvent';
import DebugModel from '../../app/debug/model/DebugModel';
import PlatformUserTrackingService from '../../app/platform/controller/PlatformUserTrackingService';
import ProgressModel from '../../app/progress/model/ProgressModel';
import SaveGameService from '../../app/savegame/controller/SaveGameService';
import SaveGameEvent from '../../app/savegame/event/SaveGameEvent';
import SaveGameModel from '../../app/savegame/model/SaveGameModel';
import AbstractCommand from '../../core/application/AbstractCommand';
import SkinKeyRegistry from '../../core/ui/SkinKeyRegistry';
import ScenarioCommandEvent from '../scenario/controller/ScenarioCommandEvent';
import ScenarioLibrary from '../scenario/ScenarioLibrary';

export default class StartGameCommand extends AbstractCommand {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {ChapterLibrary} chapterLibrary 
     * @param {ScenarioLibrary} scenarioLibrary 
     * @param {SaveGameService} saveGameService 
     * @param {SaveGameModel} saveGameModel 
     * @param {ProgressModel} progressModel 
     * @param {LanguagePack} languagePack 
     * @param {PlatformUserTrackingService} platformUserTrackingService 
     */
    constructor(dispatcher, chapterLibrary, scenarioLibrary, saveGameService, saveGameModel, progressModel, languagePack, platformUserTrackingService) {
        super(dispatcher);

        this._chapterLibrary = chapterLibrary;
        this._scenarioLibrary = scenarioLibrary;
        this._saveGameService = saveGameService;
        this._saveGameModel = saveGameModel;
        this._progressModel = progressModel;
        this._languagePack = languagePack;
        this._platformUserTrackingService = platformUserTrackingService;

        this._handleGameLoadedBound = this._handleGameLoaded.bind(this);
    }

    async execute() {
        await this._platformUserTrackingService.initialize();

        this._initializeGame();
        this.trackEvent(TrackString.EVENT_GAME_START);

        if (DebugModel.clearSavegame) {
            await this._saveGameService.clear();
        }

        this._loadGame();
    }

    _initializeGame() {
        this.dispatch(ScenarioCommandEvent.INITIALIZE_SCENARIOS);
        this.dispatch(InitializeCharactersEvent.PERFORM);
    }

    _loadGame() {
        this._dispatcher.addEventListener(SaveGameEvent.GAME_LOADED, this._handleGameLoadedBound);
        this.dispatch(SaveGameEvent.LOAD);
    }

    _handleGameLoaded(evt) {
        this._dispatcher.removeEventListener(SaveGameEvent.GAME_LOADED, this._handleGameLoadedBound);
        this._startGame();
    }

    _startGame() {
        const debugQueryEncountered = this._handleDebugQueries();
        if (debugQueryEncountered) {
            return;
        }

        // Ensure that the player has seen the intro sequence.
        if (!this._saveGameModel.hasSeenIntroSequence) {
            this.openScreen(SkinKeyRegistry.GAME_INTRODUCTION_SCREEN_SKIN);
            return;
        }

        // Otherwise we continue where we left off.
        const completedScenarioIds = this._progressModel.completedScenarioIds;
        const nextScenarioToPlay = this._scenarioLibrary.scenarios.find(scenario => !completedScenarioIds.includes(scenario.id));
        if (!nextScenarioToPlay) {
            // All scenarios are completed.
            // Show the game outro sequence.
            this.openScreen(SkinKeyRegistry.GAME_OUTRO_SCREEN_SKIN);
            return;
        }
        this.dispatch(ScenarioCommandEvent.PLAY_SCENARIO, { scenario: nextScenarioToPlay });
    }

    /**
     * @returns {boolean} Whether a debug query was handled (this should generally not continue the normal game flow).
     */
    _handleDebugQueries() {
        if (DebugModel.crash) {
            callingUndefinedFunctionForTestingPurposes();
        }

        if (DebugModel.gotoEnd) {
            this.openScreen(SkinKeyRegistry.END_OF_GAME_SCREEN_SKIN);
            return true;
        }

        if (DebugModel.hasScenarioOverride()) {
            const debugScenario = DebugModel.scenario;
            const scenario = this._scenarioLibrary.getScenarioByID(debugScenario);
            this.dispatch(ScenarioCommandEvent.PLAY_SCENARIO, { scenario });
            return true;
        }

        if (DebugModel.skipIntro) {
            // Play the first scenario.
            this.dispatch(ScenarioCommandEvent.PLAY_SCENARIO);
            return true;
        }

        return false;
    }
}